import { API_BASE } from './client.js'

export interface PublicOrderModifier {
  name: string
  price: number
  quantity: number
}

export interface PublicOrderItem {
  productName: string
  variantName: string | null
  quantity: number
  unitPrice: number
  totalPrice: number
  modifiers: PublicOrderModifier[]
}

export interface PublicOrderStatusLogEntry {
  status: string
  changedAt: string
}

export interface PublicOrderResponse {
  orderNumber: string
  status: string
  type: string
  createdAt: string
  total: number
  currency: string
  cancellable: boolean
  statusLog: PublicOrderStatusLogEntry[]
  organization: {
    name: string
    slug: string
    logoUrl: string | null
  }
  location: {
    name: string
    address: string | null
  }
  items: PublicOrderItem[]
}

export class PublicOrderError extends Error {
  status: number

  constructor(status: number, message: string) {
    super(message)
    this.status = status
  }
}

export async function getPublicOrder(publicToken: string): Promise<PublicOrderResponse> {
  const res = await fetch(`${API_BASE}/public/orders/${encodeURIComponent(publicToken)}`)
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: { message: `Request failed (${res.status})` } }))
    throw new PublicOrderError(res.status, body?.error?.message ?? `Request failed (${res.status})`)
  }
  const json = await res.json()
  return json.data as PublicOrderResponse
}

export interface CancelOrderData {
  reason?: string
}

export async function cancelPublicOrder(publicToken: string, reason?: string): Promise<PublicOrderResponse> {
  const res = await fetch(`${API_BASE}/public/orders/${encodeURIComponent(publicToken)}/cancel`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason }),
  })
  if (!res.ok) {
    let msg = `Request failed (${res.status})`
    try {
      const body = await res.json()
      msg = body?.error?.message ?? msg
    } catch {}
    throw new PublicOrderError(res.status, msg)
  }
  const json = await res.json()
  return json.data as PublicOrderResponse
}